import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import { motion } from 'framer-motion';

const affiliations = [
  { name: 'Applanix', image: '/images/Applanix.png' },
  { name: 'Track Air', image: '/images/track.png' },
  { name: 'Vexcel Imaging', image: '/images/Vexcel.jfif' },
  { name: 'Riegl', image: '/images/Riegl.png' },
  { name: 'Trimble', image: '/images/Trimble.png' },
  { name: 'Esri', image: '/images/Esri.jfif' },
  { name: 'TerraSolid', image: '/images/terra.jfif' },
  { name: 'Spectra Precision', image: '/images/spectra.png' },
];

const countyGovernments = [
  { name: 'Nairobi City County', image: '/images/nairobi.png' },
  { name: 'Turkana County', image: '/images/turkana.jfif' },
  { name: 'Nakuru County', image: '/images/nakuru.png' },
  { name: 'Kajiado County', image: '/images/kajiado.jfif' },
  { name: 'Mombasa County', image: '/images/mombasa.png' },
  { name: 'Trans-Nzoia County', image: '/images/nzoia.png' },
  { name: 'Muranga County', image: '/images/muranga.jfif' },
  { name: 'Lamu County', image: '/images/lamu.jfif' },
];

const internationalClients = [
  { name: 'Rwanda', image: '/images/rwanda.png' },
  { name: 'Uganda', image: '/images/uganda.png' },
  { name: 'DRC', image: '/images/drc.png' },
  { name: 'Senegal', image: '/images/senegal.png' },
  { name: 'Mozambique', image: '/images/mozambique.png' },
  { name: 'Lesotho', image: '/images/lesotho.png' },
  { name: 'Kuwait', image: '/images/kuwait.png' },
  { name: 'France', image: '/images/france.png' },
];

const corporatePartners = [
  { name: 'KETRACO', image: '/images/ketraco.jfif' },
  { name: 'KenGen', image: '/images/kengen.png' },
  { name: 'KPC', image: '/images/kpc.png' },
  { name: 'LAPSSET', image: '/images/lapsset.png' },
  { name: 'Kenya Power', image: '/images/kplc.png' },
  { name: 'SGS', image: '/images/sgs.png' },
  { name: 'Howard Humphreys', image: '/images/howard.png' },
  { name: 'FAO', image: '/images/fao.png' },
  { name: 'ZAKHEM', image: '/images/zakhem.png' },
  { name: 'Northern Corridor', image: '/images/northern.png' },
  { name: 'Athi Water', image: '/images/athi.jfif' },
  { name: 'Elicio', image: '/images/elicio.png' },
];

const developmentPartners = [
  { name: 'JICA', image: '/images/jica.png' },
  { name: 'World Bank', image: '/images/worldbank.png' },
  { name: 'African Development Bank', image: '/images/afdb.png' },
  { name: 'IFC', image: '/images/ifc.png' },
];

const PartnerSection = ({ title, partners, description }) => (
  <Box sx={{ mb: 8 }}>
    <Typography
      variant="h3"
      sx={{
        fontSize: { xs: '1.75rem', md: '2.5rem' },
        fontWeight: 700,
        mb: 2,
        color: 'primary.main',
      }}
    >
      {title}
    </Typography>
    {description && (
      <Typography
        variant="body1"
        sx={{ mb: 4, maxWidth: '800px', color: 'text.secondary' }}
      >
        {description}
      </Typography>
    )}
    <Grid container spacing={4}>
      {partners.map((partner, index) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-8px)',
                },
              }}
            >
              <Box
                component="img"
                src={partner.image}
                alt={partner.name}
                sx={{
                  width: '100%',
                  height: '120px',
                  objectFit: 'contain',
                  mb: 2,
                }}
              />
              <Typography
                variant="subtitle1"
                align="center"
                sx={{ fontWeight: 600 }}
              >
                {partner.name}
              </Typography>
            </Card>
          </motion.div>
        </Grid>
      ))}
    </Grid>
  </Box>
);

const Partners = () => {
  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: '50vh',
          display: 'flex',
          alignItems: 'center',
          paddingTop: { xs: '160px', md: '180px' },
          paddingBottom: '60px',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("/images/partners.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: 0,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 1,
          },
        }}
      >
        <Container sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h1"
              color="white"
              sx={{
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 700,
                mb: 2,
              }}
            >
              Institutional Affiliates
            </Typography>
            <Typography
              variant="h5"
              color="white"
              sx={{
                maxWidth: '800px',
                opacity: 0.9,
              }}
            >
              Building strong partnerships to deliver innovative geospatial solutions globally.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Partners Sections */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#f8f9fa' }}>
        <Container>
          <PartnerSection
            title="Affiliations"
            partners={affiliations}
            description="Geomaps maintains the highest standards through prestigious industry accreditations and hold various professional registrations in Kenya and beyond."
          />

          <PartnerSection
            title="County Governments"
            partners={countyGovernments}
            description="County Governments: Geomaps Africa Ltd has been involved in development of County GIS databases, Land Information Management Systems, Digital mapping, Revenue enhancement using GIS systems development and Development of Automated systems to manage County Activities e.g. Land Rates, Revenue Collection, Revenue Streams Mapping, Land Management Etc."
          />

          <PartnerSection
            title="International Clients"
            partners={internationalClients}
            description="Geomaps Africa Ltd has experience in executing geospatial projects internationally that include GIS development projects, aerial mapping, Land Information Management Systems development, Cartographic Survey and infrastructure development and GIS Systems in Kigali-Rwanda, Democratic Republic of Congo, Lesotho, Uganda, Mozambique, Kuwait, Senegal to mention but a few."
          />

          <PartnerSection
            title="Corporate Clients"
            partners={corporatePartners}
          />

          <PartnerSection
            title="Development Partners"
            partners={developmentPartners}
            description="Geomaps Africa Ltd has in its 43 years of consultancy in offering geospatial solutions been privileged to work with some of the top organizations that include World Bank/African Development Bank funded projects, government parastatals, government bodies, private multi-national corporations and regional organizations."
          />
        </Container>
      </Box>
    </Box>
  );
};

export default Partners; 